import { AZURE_MAX_FILE_SIZE_BYTES, getExtension } from './AzureFileUpload.Functions';
import { IMPORTABLE_FILE_EXTENSIONS } from '../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import {
  InboxInvalidReason,
  InboxRow,
  InboxRowResolution,
  InboxRowStatus,
  ProviderInboxIdentifyResult,
  ProviderInboxResolveRequest,
} from '../models/Entities/ProviderInbox/ProviderInbox';

/*
 * Alta de proveedor desde la factura (spec 009, US4): validación local del archivo, CUIT, qué hacer después de leer el
 * archivo y a dónde navegar. Funciones puras, sin React.
 */

/** Extensiones que acepta la bandeja (pdf, docx, xlsx, xls, csv). */
export const INBOX_FILE_EXTENSIONS = IMPORTABLE_FILE_EXTENSIONS;
/** Tamaño máximo de la bandeja (50 MB, igual que el resto de los archivos). */
export const INBOX_MAX_FILE_SIZE_BYTES = AZURE_MAX_FILE_SIZE_BYTES;
/**
 * Por encima de este tamaño el gateway puede cortar la subida antes de llegar a la API (su límite por defecto ronda los
 * 30 MB): un 413 o una falla de red se explican como "archivo grande".
 */
export const INBOX_GATEWAY_SIZE_HINT_BYTES = 28 * 1024 * 1024;
/** Lecturas (identify) en paralelo como máximo (FR-021). */
export const INBOX_MAX_PARALLEL = 3;
/** Valor del atributo `accept` del selector de archivos. */
export const INBOX_ACCEPT = INBOX_FILE_EXTENSIONS.map((extension) => `.${extension}`).join(',');

/** Motivo para no mandar el archivo (sin llamar a la API); undefined si se puede leer. */
export const validateInvoiceFile = (file: Pick<File, 'name' | 'size'>): InboxInvalidReason | undefined => {
  if (!INBOX_FILE_EXTENSIONS.includes(getExtension(file.name))) return 'extension';
  if (file.size > INBOX_MAX_FILE_SIZE_BYTES) return 'size';
  if (file.size === 0) return 'empty';
  return undefined;
};

/** Sólo los dígitos si el valor tiene únicamente dígitos y separadores (`-`, `.`, `/`, espacios); si no, undefined. */
export const normalizeTaxId = (value: string | null | undefined): string | undefined => {
  const text = (value ?? '').trim();
  if (!text || !/^[\d\-./ ]+$/.test(text)) return undefined;
  const digits = text.replace(/\D/g, '');
  return digits || undefined;
};

const TAX_ID_WEIGHTS = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];

/** CUIT / CUIL: 11 dígitos con verificador módulo 11 (igual que `TaxIdValidator` de MP; con resto 1 no es válido). */
export const isValidTaxId = (value: string | null | undefined): boolean => {
  const digits = normalizeTaxId(value);
  if (!digits || digits.length !== 11) return false;
  const sum = TAX_ID_WEIGHTS.reduce((total, weight, index) => total + Number(digits[index]) * weight, 0);
  let check = 11 - (sum % 11);
  if (check === 11) check = 0;
  else if (check === 10) return false;
  return Number(digits[10]) === check;
};

/** "20-28589697-6" (sólo con 11 dígitos; si no, el valor tal cual). */
export const formatTaxId = (value: string | null | undefined): string => {
  const digits = normalizeTaxId(value);
  if (!digits || digits.length !== 11) return (value ?? '').trim();
  return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
};

/** Estado de la fila después de leer el archivo: se resuelve sola, faltan datos o no se puede. */
export const rowStatusAfterIdentify = (result: ProviderInboxIdentifyResult): InboxRowStatus => {
  if (result.proposedAction === 'Reject') return 'rejected';
  return autoResolveRequest(result) ? 'resolving' : 'needsInput';
};

/**
 * Resolución automática (DEC-006): `UseExisting` con el proveedor encontrado o `CreateProvider` con la razón social y el
 * CUIT válido del emisor. undefined si hace falta que el usuario complete los datos.
 */
export const autoResolveRequest = (result: ProviderInboxIdentifyResult): ProviderInboxResolveRequest | undefined => {
  if (result.proposedAction === 'UseExisting' && result.matchedProvider?.providerId) return { providerId: result.matchedProvider.providerId };
  if (result.proposedAction === 'CreateProvider') {
    const businessName = result.voucher?.issuer?.businessName?.trim();
    const taxId = normalizeTaxId(result.voucher?.issuer?.taxId);
    if (businessName && taxId && isValidTaxId(taxId)) return { create: { businessName, taxId } };
  }
  return undefined;
};

/** Pedido de "Completar": razón social recortada y CUIT normalizado sólo si se cargó (DEC-APP-002). */
export const manualResolveRequest = (values: { businessName: string; taxId?: string | null }): ProviderInboxResolveRequest => {
  const taxId = normalizeTaxId(values.taxId);
  return { create: taxId ? { businessName: values.businessName.replace(/\s+/g, ' ').trim(), taxId } : { businessName: values.businessName.replace(/\s+/g, ' ').trim() } };
};

/** Clave para encadenar las resoluciones del mismo CUIT (dos facturas del mismo emisor no crean dos proveedores). */
export const resolveChainKey = (result: ProviderInboxIdentifyResult | undefined, request?: ProviderInboxResolveRequest): string | undefined => {
  if (request && 'create' in request && request.create.taxId) return `cuit:${request.create.taxId}`;
  if (request && 'providerId' in request) return `provider:${request.providerId}`;
  const taxId = normalizeTaxId(result?.voucher?.issuer?.taxId);
  return taxId ? `cuit:${taxId}` : undefined;
};

/** Ruta interna del proveedor con el asistente abierto en el archivo; nunca navega fuera de /providers/. */
export const safeImportRoute = (resolved: Pick<InboxRowResolution, 'providerId' | 'providerFileId' | 'importUrl'>): string => {
  const url = resolved.importUrl?.trim();
  if (url && /^\/providers\/\d+(\?[^#\s]*)?$/.test(url) && !url.includes('//')) return url;
  const base = `/providers/${encodeURIComponent(String(resolved.providerId))}?tab=consumptions`;
  return resolved.providerFileId ? `${base}&importFileId=${encodeURIComponent(resolved.providerFileId)}` : base;
};

const RESOLVED: InboxRowStatus[] = ['resolvedExisting', 'resolvedCreated', 'discarded'];

/** Filas con archivo en la bandeja que no se resolvieron (se descartan al cerrar). */
export const pendingInboxIds = (rows: InboxRow[]): string[] =>
  rows.filter((row) => !!row.identify?.inboxId && !RESOLVED.includes(row.status)).map((row) => row.identify!.inboxId);

/** Filas que todavía se están leyendo o resolviendo (al cerrar, su lectura se descarta cuando termine). */
export const isRowBusy = (row: InboxRow): boolean => row.status === 'queued' || row.status === 'reading' || row.status === 'resolving';

/** Filas que cuentan como "sin resolver" para la confirmación del cierre. */
export const unresolvedCount = (rows: InboxRow[]): number => pendingInboxIds(rows).length + rows.filter((row) => isRowBusy(row) && !row.identify).length;

/** El error de la lectura se debe (probablemente) al tamaño: 413 o falla de red con un archivo grande. */
export const isLikelyTooLarge = (code: number, size: number): boolean => code === 413 || (code === 0 && size > INBOX_GATEWAY_SIZE_HINT_BYTES);
